import { useCallback, useEffect, useRef, useState } from 'react';
import { inviteService } from '../services/AssessmentService';
import {
  SUBMIT_SELECTOR,
  createThrottledWriter,
  findSubmitButton,
  isSubmitClick,
  readAnswers,
  readProgress,
  readResult,
} from '../utils/questionnaireTracking';

/** Progress changes are batched into one write per this interval. */
const WRITE_INTERVAL_MS = 5 * 1000;
/** Clicks and typing without progress only refresh "zuletzt aktiv" this often. */
const ACTIVITY_INTERVAL_MS = 60 * 1000;

const nowIso = () => new Date().toISOString();

/**
 * What the submit button says inside our frame. The document calls it
 * "Antworten per E-Mail senden", because a file on a desktop has no other way
 * out — here the answers go straight into the invitation, and a candidate who
 * reads "E-Mail" looks for a mail they do not have to write.
 *
 * The file itself stays untouched: it is handed out as a file too, and 27 of
 * them would have to be re-edited.
 */
const SUBMIT_LABEL = '\u2713 Antworten absenden';

/**
 * Reports a candidate's Fragebogen 2 session back to the invitation: opened,
 * progress, last activity and the click on "senden" — and takes that click
 * over, so the candidate gets our confirmation instead of a mail draft.
 *
 * The frame is same-origin (srcdoc, no sandbox), so its document can be read
 * directly. Returns the frame's onLoad handler plus what the page needs to
 * show for the submit.
 */
export function useInviteTracking(invite) {
  const writerRef = useRef(null);
  const docRef = useRef(null);
  const progressRef = useRef(0);
  const detachRef = useRef(null);
  const submitButtonRef = useRef(null);
  // Set while we re-dispatch the click ourselves, so that one goes through to
  // the document and opens its mail — the fallback when our write failed.
  const passThroughRef = useRef(false);
  const [submitState, setSubmitState] = useState(null);

  // The frame's load and the effect below race: whichever comes second
  // attaches, so a questionnaire that loads before the writer exists is still
  // observed instead of silently reporting only "geöffnet".
  const attach = useCallback(() => {
    const doc = docRef.current;
    const writer = writerRef.current;
    if (!doc || !writer) return;
    detachRef.current?.();

    const reportProgress = () => {
      const progress = readProgress(doc);
      if (progress === null || progress === progressRef.current) return;
      progressRef.current = progress;
      writer.push({ progress, last_active_at: nowIso() });
    };
    // Picks up progress restored from the candidate's earlier visit.
    reportProgress();

    const progressElement = doc.getElementById('ppct');
    const observer = progressElement ? new MutationObserver(reportProgress) : null;
    observer?.observe(progressElement, { childList: true, characterData: true, subtree: true });

    submitButtonRef.current = findSubmitButton(doc);
    if (submitButtonRef.current) submitButtonRef.current.textContent = SUBMIT_LABEL;

    const submit = async (button) => {
      const at = nowIso();
      setSubmitState('sending');
      // The answers travel with the submit, not with every progress write:
      // this is the one moment the candidate declares the questionnaire done.
      // The document is not asked for anything — its answers are simply read
      // out of the frame.
      const stored = await writer.push(
        {
          status: 'submitted',
          submitted_at: at,
          last_active_at: at,
          progress: readProgress(doc) ?? progressRef.current,
          answers: readAnswers(doc),
          result: readResult(doc),
        },
        { immediate: true }
      );
      if (stored) {
        setSubmitState('stored');
        return;
      }
      // The answers did not land, so the document's mail is the only thing
      // carrying them. Hand the click back rather than claim an arrival.
      setSubmitState(null);
      passThroughRef.current = true;
      button?.click();
    };

    let lastActivity = 0;
    const onActivity = (activity) => {
      if (activity.type === 'click' && isSubmitClick(activity.target)) {
        if (passThroughRef.current) {
          passThroughRef.current = false;
          return;
        }
        // Stopping here, in the capture phase, keeps the click from reaching
        // the button's own onclick: no mail draft opens while our write runs,
        // and none at all when it succeeds.
        activity.stopPropagation();
        activity.preventDefault();
        submit(activity.target.closest(SUBMIT_SELECTOR));
        return;
      }
      if (Date.now() - lastActivity < ACTIVITY_INTERVAL_MS) return;
      lastActivity = Date.now();
      writer.push({ last_active_at: nowIso() });
    };
    doc.addEventListener('click', onActivity, true);
    doc.addEventListener('input', onActivity, true);

    detachRef.current = () => {
      observer?.disconnect();
      doc.removeEventListener('click', onActivity, true);
      doc.removeEventListener('input', onActivity, true);
    };
  }, []);

  useEffect(() => {
    if (!invite) return undefined;

    progressRef.current = invite.progress ?? 0;
    const writer = createThrottledWriter(
      (fields, options) => inviteService.track(invite, fields, options),
      WRITE_INTERVAL_MS
    );
    writerRef.current = writer;

    const firstOpen = invite.openedAt ? {} : { status: 'opened', opened_at: nowIso() };
    writer.push({ ...firstOpen, last_active_at: nowIso() }, { immediate: true });
    attach();

    const flushOnLeave = () => writer.flush({ keepalive: true });
    window.addEventListener('pagehide', flushOnLeave);
    return () => {
      window.removeEventListener('pagehide', flushOnLeave);
      detachRef.current?.();
      detachRef.current = null;
      writerRef.current = null;
      writer.flush({ keepalive: true });
    };
  }, [invite, attach]);

  const onFrameLoad = useCallback(
    (event) => {
      docRef.current = event.currentTarget.contentDocument;
      attach();
    },
    [attach]
  );

  /** Lets the document send its mail after all — offered on the confirmation. */
  const sendByMail = useCallback(() => {
    if (!submitButtonRef.current) return;
    passThroughRef.current = true;
    submitButtonRef.current.click();
  }, []);

  return {
    onFrameLoad,
    submitState,
    sendByMail,
    backToQuestionnaire: useCallback(() => setSubmitState(null), []),
  };
}
